import { useState, useEffect, useCallback } from 'react'
import {
    getKeywordTopicOverview,
    getKeywordTopicKeywords,
    getKeywordTopicClusters,
    getKeywordTopicInsights
} from '../core/_requests'
import {
    KeywordTopicOverview,
    KeywordTopicKeyword,
    KeywordTopicCluster,
    KeywordTopicInsights
} from '../core/_models'
import { useAuth } from '../../../modules/auth'

export const useKeywordTopicApi = () => {
    const { currentUser } = useAuth()
    const projectId = currentUser?.selectedProject?._id

    const [overview, setOverview] = useState<KeywordTopicOverview | null>(null)
    const [keywords, setKeywords] = useState<KeywordTopicKeyword[]>([])
    const [clusters, setClusters] = useState<KeywordTopicCluster[]>([])
    const [insights, setInsights] = useState<KeywordTopicInsights | null>(null)

    const [loadingOverview, setLoadingOverview] = useState(false)
    const [loadingKeywords, setLoadingKeywords] = useState(false)
    const [loadingClusters, setLoadingClusters] = useState(false)
    const [loadingInsights, setLoadingInsights] = useState(false)

    const [error, setError] = useState<string | null>(null)

    const fetchAll = useCallback(async () => {
        if (!projectId) return

        setError(null)

        // Fetch Overview
        setLoadingOverview(true)
        getKeywordTopicOverview(projectId)
            .then(res => setOverview(res.data))
            .catch(err => console.error("Overview error", err))
            .finally(() => setLoadingOverview(false))

        // Fetch Keywords
        setLoadingKeywords(true)
        getKeywordTopicKeywords(projectId)
            .then(res => setKeywords(res.data))
            .catch(err => console.error("Keywords error", err))
            .finally(() => setLoadingKeywords(false))

        // Fetch Clusters
        setLoadingClusters(true)
        getKeywordTopicClusters(projectId)
            .then(res => setClusters(res.data))
            .catch(err => console.error("Clusters error", err))
            .finally(() => setLoadingClusters(false))

        // Fetch Insights
        setLoadingInsights(true)
        getKeywordTopicInsights(projectId)
            .then(res => setInsights(res.data))
            .catch(err => console.error("Insights error", err))
            .finally(() => setLoadingInsights(false))

    }, [projectId])

    useEffect(() => {
        if (projectId) {
            fetchAll()
        }
    }, [projectId, fetchAll])

    return {
        overview,
        keywords,
        clusters,
        insights,
        loadingOverview,
        loadingKeywords,
        loadingClusters,
        loadingInsights,
        error,
        refetch: fetchAll
    }
}
